/* eslint-disable no-param-reassign */
import { displayErrorDetails } from '@brands/Utils/displayError';
import { getParsedTimezoneName } from '@brands/Utils/getParsedTimezoneName';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import {
  Box,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from '@mui/material';
import moment from 'moment-timezone';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaCheckCircle } from 'react-icons/fa';
import { HiChevronLeft } from 'react-icons/hi';

import Button from '../../../../Components/Button/Button';
import { Loading } from '../../../../Components/LoadingSpinner/Loading';
import ConfirmationModal from '../../../../Components/Modals/ConfirmationModal/ConfirmationModal';
import { useInsuranceCarriers } from '../../../../hooks';
import useDebounce from '../../../../hooks/useDebounce';
import useProviders from '../../../../hooks/useProviders';
import { IAssignProviderCase } from '../../../../services/cases/assignProvider';
import { CaseType, ICase } from '../../../../services/cases/types/ICase';
import { getUserAvatar } from '../../../../services/identity/getUserAvatar';
import { listAllLanguages } from '../../../../services/identity/listAllLanguages';
import { Insurance, License, UserProfile } from '../../../../services/identity/types/UserProfile';
import { getFriendlyName } from '../../../../Utils/getFriendlyName';
import { sortData, SortKeys } from '../../../../Utils/providerSortData';
import { OptionLanguage, specialtyOptions, stateOptions } from '../../../../Utils/selectOptions';
import ProviderProfileCardModal from '../../../ProfileCards/Provider/ProfileCards';
import { OralHealthReportSubmit, ProviderFilter } from '../../assets/types';
import SelectClearButton from '../../Components/SelectClearButton';
import styles from './providerList.module.scss';

/* ===========================
   STYLES
=========================== */

const headerCellSx = {
  backgroundColor: '#F9FAFB',
  fontSize: 12,
  fontWeight: 600,
  color: '#374151',
  borderBottom: '1px solid #E5E7EB',
  padding: '8px 12px',
  whiteSpace: 'nowrap' as const,
};

const bodyCellSx = {
  fontSize: 13,
  borderBottom: '1px solid #E5E7EB',
  padding: '10px 12px',
  verticalAlign: 'middle' as const,
};

const getFilterSelectSx = (showClearButton: boolean) => ({
  fontSize: 12,
  '& .MuiSelect-select': {
    paddingRight: showClearButton ? '56px !important' : undefined,
  },
});

/* ===========================
   COLUMN DEFINITIONS
=========================== */

const COLUMNS: { key: SortKeys; label: string }[] = [
  { key: 'full_name', label: 'Name' },
  { key: 'license_states', label: 'License state' },
  { key: 'specialty', label: 'Specialty' },
  { key: 'plan', label: 'Insurance' },
  { key: 'languages', label: 'Language' },
  { key: 'availability', label: 'Availability' },
];

const TIMEZONE_OPTIONS = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
];

/* ===========================
   SUB-COMPONENTS
=========================== */

const FilterCell = ({ children }: { children: React.ReactNode }) => (
  <TableCell sx={{ background: '#FFF', padding: '4px 8px', borderBottom: '2px solid #E5E7EB' }}>{children}</TableCell>
);

const SortableHeader = ({
  label,
  column,
  sortBy,
  sortDirection,
  onSort,
}: {
  label: string;
  column: SortKeys;
  sortBy: SortKeys | null;
  sortDirection: 'asc' | 'desc' | null;
  onSort: (col: SortKeys) => void;
}) => (
  <TableCell sx={{ ...headerCellSx, cursor: 'pointer' }} onClick={() => onSort(column)}>
    <Box display="flex" alignItems="center" gap={0.5}>
      {!sortBy || sortBy !== column ? (
        <UnfoldMoreIcon fontSize="small" />
      ) : sortDirection === 'asc' ? (
        <ArrowUpwardIcon fontSize="small" />
      ) : (
        <ArrowDownwardIcon fontSize="small" />
      )}
      {label}
    </Box>
  </TableCell>
);

/* ===========================
   PROPS
=========================== */

interface IModal {
  openModal: (arg0: boolean) => void;
  thisCase: ICase;
  setCase: React.Dispatch<React.SetStateAction<ICase>>;
  consultationForm: OralHealthReportSubmit;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  assignToProvider: (case_id: number, body: IAssignProviderCase) => Promise<ICase | null>;
}

/* ===========================
   COMPONENT
=========================== */

const ProviderListTable = ({
  openModal,
  thisCase,
  setCase,
  consultationForm,
  setLoading,
  assignToProvider,
}: IModal): JSX.Element => {
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // ── State ────────────────────────────────────────────────────────────────
  const [targetProvider, setTargetProvider] = useState<UserProfile | null>(null);
  const [languageOptions, setLanguageOptions] = useState<OptionLanguage[]>([]);
  const [patientInsurance, setPatientInsurance] = useState<Insurance | undefined>(undefined);

  const [sortBy, setSortBy] = useState<SortKeys | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [nameFilter, setNameFilter] = useState('');
  const [selectedTimezone, setSelectedTimezone] = useState(browserTimezone);

  const [providerProfileCardModal, setProviderProfileCardModal] = useState(false);
  const [selectedProvider, isSelectedProvider] = useState<number>(0);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const isSubmittingRef = useRef(false);

  // ── Insurance carriers ───────────────────────────────────────────────────
  const insuranceCarriers = useInsuranceCarriers();
  const insuranceCarrierOptions = useMemo(
    () => insuranceCarriers.map((ic) => ({ value: ic.id, label: ic.name })),
    [insuranceCarriers],
  );

  // ── API filter state ─────────────────────────────────────────────────────
  const [filters, setFilters] = useState<ProviderFilter>({
    availability: [
      thisCase.type === 'video_call_instant' ||
      thisCase.type === 'video_call_scheduled' ||
      thisCase.type === CaseType.phone_call_scheduled
        ? 'CaseType.video_call_instant'
        : 'CaseType.one_report',
    ],
    synchronous:
      thisCase.type === 'video_call_instant' ||
      thisCase.type === 'video_call_scheduled' ||
      thisCase.type === CaseType.phone_call_scheduled,
    asynchronous: thisCase.type === 'one_report' || thisCase.type === 'second_opinion',
    license_states: [stateOptions.find((state) => state.value === thisCase.patient_current_state)!],
    out_of_network: !thisCase.insurance?.insurance_membership?.insurance_carrier?.id,
  });

  // ── Effects ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const insurances: Insurance[] = thisCase?.patient?.patient_profile?.insurances ?? [];
    setPatientInsurance(insurances[0]);
  }, [thisCase]);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      ...(thisCase.insurance?.insurance_membership?.insurance_carrier?.name && {
        carrier_id:
          insuranceCarrierOptions.find(
            (plan) => plan.value === thisCase?.insurance?.insurance_membership?.insurance_carrier?.id || 0,
          )?.value || 0,
      }),
      out_of_network: prev.carrier_id === undefined,
    }));
  }, [insuranceCarrierOptions]);

  useEffect(() => {
    listAllLanguages().then((data) => {
      setLanguageOptions(data.map((l) => ({ value: l.locale, label: l.name, id: l.id })));
    });
  }, []);

  useEffect(() => {
    if (thisCase?.type === 'one_report') {
      setFilters((prev) => ({
        ...prev,
        availability: ['CaseType.one_report'],
        asynchronous: true,
        synchronous: false,
      }));
    }
  }, [thisCase]);

  // ── API params ───────────────────────────────────────────────────────────
  const params = useMemo(() => {
    const videoConferenceDateUTC = moment.utc(thisCase.video_conference_date);
    const currentLocalTime = moment().tz(browserTimezone);
    const utcTime = currentLocalTime.utc();
    return {
      date:
        thisCase.type === CaseType.video_call_scheduled || thisCase.type === CaseType.phone_call_scheduled
          ? videoConferenceDateUTC.isBefore(utcTime)
            ? moment().format('YYYY-MM-DD')
            : videoConferenceDateUTC.tz(browserTimezone).format('YYYY-MM-DD')
          : moment(new Date()).format('YYYY-MM-DD'),
      tz: browserTimezone,
      ...(process.env.REACT_APP_APP_BRAND === 'dentistry-one' ? { lookahead_days: 13 } : {}),
      specialty: filters.specialty ? filters.specialty.map((i) => i.value) : undefined,
      language: filters.language ? filters.language.map((i) => i.label) : undefined,
      carrier_id: filters.carrier_id ? Number(filters.carrier_id) : undefined,
      license_states: filters.license_states ? filters.license_states.map((i) => i.label.split(' - ')[0]) : undefined,
      ...(filters.asynchronous ? { asynchronous: filters.asynchronous } : { asynchronous: false }),
      ...(filters.synchronous ? { synchronous: filters.synchronous } : { synchronous: false }),
      case_id: Number(thisCase.id),
      ...(filters.out_of_network !== undefined && filters.carrier_id && { out_of_network: false }),
      patient_id: Number(thisCase.patient_id),
    };
  }, [filters, thisCase.id, thisCase.patient_id, thisCase.type, thisCase.video_conference_date, browserTimezone]);

  const debouncedParams = useDebounce(params, 500);
  const { providers, dataLoading, loadError, reloadAll } = useProviders(debouncedParams);

  useEffect(() => {
    if (selectedProvider && !providers.find((p) => p.id === selectedProvider)) {
      isSelectedProvider(providers[0]?.id || 0);
    }
  }, [providers]);

  // ── Sort ─────────────────────────────────────────────────────────────────
  const handleSort = (column: SortKeys) => {
    setSortBy((prev) => {
      if (prev !== column) {
        setSortDirection('asc');
        return column;
      }
      if (sortDirection === 'asc') {
        setSortDirection('desc');
        return column;
      }
      setSortDirection(null);
      return null;
    });
  };

  // ── Derived data ─────────────────────────────────────────────────────────
  const sortedProviders = useMemo(() => {
    if (!sortBy) return providers;
    return sortData({ tableData: providers, sortKey: sortBy, reverse: sortDirection === 'desc' });
  }, [providers, sortBy, sortDirection]);

  const filteredData = useMemo(() => {
    return sortedProviders.filter((p) => {
      if (p.user?.id === thisCase.provider_id) return false;
      if (nameFilter && !p.user.full_name.toLowerCase().includes(nameFilter.toLowerCase())) return false;
      return true;
    });
  }, [sortedProviders, nameFilter, thisCase.provider_id]);

  const paginatedData = useMemo(
    () => filteredData.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [filteredData, page, rowsPerPage],
  );

  // ── Column filter derived values ─────────────────────────────────────────
  const stateFilterValue = useMemo(() => {
    if (!filters.license_states || filters.license_states.length !== 1) return '';
    return filters.license_states[0].value || '';
  }, [filters.license_states]);

  const specialtyFilterValue = useMemo(() => {
    if (!filters.specialty || filters.specialty.length !== 1) return '';
    return (filters.specialty[0] as any).value || '';
  }, [filters.specialty]);

  const insuranceFilterValue = useMemo(
    () => (filters.carrier_id ? String(filters.carrier_id) : ''),
    [filters.carrier_id],
  );

  const languageFilterValue = useMemo(() => {
    if (!filters.language || filters.language.length !== 1) return '';
    return (filters.language[0] as any).value || '';
  }, [filters.language]);

  const availabilityFilterValue = useMemo(() => {
    if (filters.synchronous && filters.asynchronous) return 'all';
    if (filters.synchronous) return 'sync';
    if (filters.asynchronous) return 'async';
    return 'all';
  }, [filters.synchronous, filters.asynchronous]);

  // ── Timezone options (include browser tz if not in presets) ──────────────
  const timezoneOptions = useMemo(() => {
    const opts = [...TIMEZONE_OPTIONS];
    if (!opts.includes(browserTimezone)) opts.unshift(browserTimezone);
    return opts;
  }, [browserTimezone]);

  // ── Cell helpers ─────────────────────────────────────────────────────────
  const getLicenseStates = (licenses: License[]): string => {
    const today = moment().format('YYYY-MM-DD');
    return licenses
      .filter((l) => l.expiry_date && moment(l.expiry_date).isAfter(today))
      .map((l) => l.state)
      .join(', ');
  };

  const getInsurances = (licenses: License[]): string =>
    licenses
      .flatMap((l) => l.insurance_carriers?.flatMap((c) => c.insurance_carrier?.name) ?? [])
      .filter(Boolean)
      .join(', ');

  // ── Assign ───────────────────────────────────────────────────────────────
  // Sync guard against double-fire on the Confirm button (same pattern as DO-4019)
  const assignProvider = async (providerId: number): Promise<void> => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setLoading(true);
    try {
      localStorage.removeItem('notes');
      if (thisCase) {
        const updatedCase = await assignToProvider(thisCase.id, { provider_id: providerId });
        if (updatedCase) {
          setCase({
            ...thisCase,
            status: updatedCase.status,
            provider: updatedCase.provider,
            provider_id: updatedCase.provider_id,
            updated_at: updatedCase.updated_at,
          });
        }
      }
    } catch (error: unknown) {
      displayErrorDetails(error);
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
    }
  };

  const handleSelectedProvider = (target: number): void => {
    isSelectedProvider((prev) => (prev === target ? 0 : target));
  };

  const selectedProviderName = useMemo(() => {
    const provider = providers.find((p) => p.id === selectedProvider);
    return provider?.user.full_name || '';
  }, [providers, selectedProvider]);

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className={styles.modalBackground}>
      <div className={styles.modalContainer}>
        <div className={styles.providerListContainer}>
          {/* Back */}
          <button type="button" className={styles.backLink} onClick={() => openModal(false)}>
            <HiChevronLeft />
            Back
          </button>

          {/* Patient Info */}
          <div className={styles.providerListHeaderPatient}>
            <span>Patient Info</span>
            <div className={styles.patientInfo}>
              <span className={styles.container}>
                <span className="fs-unmask">Name</span>
                <p className="fs-exclude">{thisCase?.patient.full_name}</p>
              </span>
              <span className={styles.container}>
                <span className="fs-unmask">Case type</span>
                <p className="fs-unmask">{getFriendlyName(thisCase.type)}</p>
              </span>
              <span className={styles.container}>
                <span className="fs-unmask">Location</span>
                <p className="fs-exclude">{thisCase?.patient_current_state}</p>
              </span>
              <span className={styles.container}>
                <span className="fs-unmask">Time zone</span>
                <p className="fs-unmask">
                  {thisCase?.patient?.address?.timezone
                    ? getParsedTimezoneName(thisCase.patient.address.timezone)
                    : '-'}
                </p>
              </span>
              <span className={styles.container}>
                <span className="fs-unmask">Insurance</span>
                <p className="fs-exclude">
                  {thisCase?.insurance?.id
                    ? thisCase?.insurance?.insurance_membership?.insurance_carrier?.name
                    : patientInsurance
                    ? patientInsurance.insurance_membership?.insurance_carrier?.name
                    : '-'}
                </p>
              </span>
              <span className={styles.container}>
                <span className="fs-unmask">Language(s)</span>
                <p className="fs-mask">
                  {thisCase?.patient?.basic_info?.languages
                    ?.map((language) => typeof language !== 'string' && language.name)
                    .join(', ')}
                </p>
              </span>
              <span className={styles.container}>
                <span className="fs-unmask">Payment method</span>
                <p className="fs-unmask">
                  {thisCase?.pricing?.amount_after_plan === 0
                    ? 'N/A'
                    : thisCase?.insurance?.id
                    ? 'Insurance'
                    : 'Credit Card'}
                </p>
              </span>
            </div>
          </div>

          {/* Title + action buttons */}
          <div className={`row ${styles.providerListHeader}`}>
            <span>Select a new provider</span>
            <div className={styles.btnGroup}>
              <Button
                className={`fs-unmask ${styles.btn}`}
                onClick={() => setShowConfirmDialog(true)}
                disabled={selectedProvider === 0}
              >
                Assign Provider
              </Button>
              <button className={`fs-unmask ${styles.cBtn}`} type="button" onClick={() => openModal(false)}>
                Cancel
              </button>
            </div>
          </div>

          {/* Timezone selector */}
          <div className={styles.timezoneFilter}>
            <span className={`fs-unmask ${styles.timezoneLabel}`}>Timezone</span>
            <FormControl variant="standard" size="small">
              <Select
                value={selectedTimezone}
                onChange={(e) => setSelectedTimezone(e.target.value)}
                disableUnderline
                sx={{ fontSize: 14, fontWeight: 500, color: '#172433' }}
              >
                {timezoneOptions.map((tz) => (
                  <MenuItem key={tz} value={tz}>
                    {getParsedTimezoneName(tz)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          {process.env.REACT_APP_APP_BRAND === 'dentistry-one' && <p>Availability for the next 14 days from the case date, or today for past appointments. Reassigning a provider does not change the appointment time.</p>}
          {loadError && <div role="alert">{loadError} <button type="button" onClick={() => void reloadAll()}>Retry</button></div>}

          {/* Table */}
          <div className={styles.providerListScroll}>
            <Paper
              elevation={0}
              sx={{ border: '1px solid #E5E7EB', borderRadius: 2, overflow: 'hidden', height: '-webkit-fit-content' }}
            >
              <TableContainer>
                <Table stickyHeader size="small">
                  <TableHead>
                    {/* Sort header row */}
                    <TableRow>
                      {COLUMNS.map((col) => (
                        <SortableHeader
                          key={col.key}
                          label={col.label}
                          column={col.key}
                          sortBy={sortBy}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                        />
                      ))}
                      <TableCell sx={headerCellSx} />
                    </TableRow>

                    {/* Filter row */}
                    <TableRow>
                      {/* Provider name */}
                      <FilterCell>
                        <TextField
                          variant="standard"
                          placeholder="Provider name"
                          value={nameFilter}
                          onChange={(e) => {
                            setNameFilter(e.target.value);
                            setPage(0);
                          }}
                          fullWidth
                          InputProps={{ sx: { fontSize: 12 } }}
                        />
                      </FilterCell>

                      {/* License state */}
                      <FilterCell>
                        <FormControl variant="standard" fullWidth sx={{ position: 'relative' }}>
                          <Select
                            displayEmpty
                            value={stateFilterValue}
                            onChange={(e) => {
                              setPage(0);
                              const val = e.target.value as string;
                              if (!val) {
                                setFilters((prev) => ({ ...prev, license_states: undefined }));
                              } else {
                                const opt = stateOptions.find((s) => s.value === val);
                                setFilters((prev) => ({ ...prev, license_states: opt ? [opt] : undefined }));
                              }
                            }}
                            sx={getFilterSelectSx(Boolean(stateFilterValue))}
                          >
                            <MenuItem value="">
                              <em style={{ fontStyle: 'normal', color: '#9CA3AF' }}>License state</em>
                            </MenuItem>
                            {stateOptions.map((s) => (
                              <MenuItem key={s.value} value={s.value}>
                                {s.value}
                              </MenuItem>
                            ))}
                          </Select>
                          <SelectClearButton
                            show={Boolean(stateFilterValue)}
                            label="license state filter"
                            onClear={() => {
                              setPage(0);
                              setFilters((prev) => ({ ...prev, license_states: undefined }));
                            }}
                          />
                        </FormControl>
                      </FilterCell>

                      {/* Specialty */}
                      <FilterCell>
                        <FormControl variant="standard" fullWidth sx={{ position: 'relative' }}>
                          <Select
                            displayEmpty
                            value={specialtyFilterValue}
                            onChange={(e) => {
                              setPage(0);
                              const val = e.target.value as string;
                              if (!val) {
                                setFilters((prev) => ({ ...prev, specialty: undefined }));
                              } else {
                                const opt = specialtyOptions.find((s) => s.value === val);
                                setFilters((prev) => ({
                                  ...prev,
                                  specialty: opt ? ([opt] as any) : undefined,
                                }));
                              }
                            }}
                            sx={getFilterSelectSx(Boolean(specialtyFilterValue))}
                          >
                            <MenuItem value="">
                              <em style={{ fontStyle: 'normal', color: '#9CA3AF' }}>Specialty</em>
                            </MenuItem>
                            {specialtyOptions.map((s) => (
                              <MenuItem key={s.value} value={s.value}>
                                {s.label}
                              </MenuItem>
                            ))}
                          </Select>
                          <SelectClearButton
                            show={Boolean(specialtyFilterValue)}
                            label="specialty filter"
                            onClear={() => {
                              setPage(0);
                              setFilters((prev) => ({ ...prev, specialty: undefined }));
                            }}
                          />
                        </FormControl>
                      </FilterCell>

                      {/* Insurance */}
                      <FilterCell>
                        <FormControl variant="standard" fullWidth sx={{ position: 'relative' }}>
                          <Select
                            displayEmpty
                            value={insuranceFilterValue}
                            onChange={(e) => {
                              setPage(0);
                              const val = e.target.value as string;
                              setFilters((prev) => ({
                                ...prev,
                                carrier_id: val ? Number(val) : undefined,
                                out_of_network: !val,
                              }));
                            }}
                            sx={getFilterSelectSx(Boolean(insuranceFilterValue))}
                          >
                            <MenuItem value="">
                              <em style={{ fontStyle: 'normal', color: '#9CA3AF' }}>Insurance</em>
                            </MenuItem>
                            {insuranceCarrierOptions.map((i) => (
                              <MenuItem key={i.value} value={String(i.value)}>
                                {i.label}
                              </MenuItem>
                            ))}
                          </Select>
                          <SelectClearButton
                            show={Boolean(insuranceFilterValue)}
                            label="insurance filter"
                            onClear={() => {
                              setPage(0);
                              setFilters((prev) => ({
                                ...prev,
                                carrier_id: undefined,
                                out_of_network: true,
                              }));
                            }}
                          />
                        </FormControl>
                      </FilterCell>

                      {/* Language */}
                      <FilterCell>
                        <FormControl variant="standard" fullWidth sx={{ position: 'relative' }}>
                          <Select
                            displayEmpty
                            value={languageFilterValue}
                            onChange={(e) => {
                              setPage(0);
                              const val = e.target.value as string;
                              if (!val) {
                                setFilters((prev) => ({ ...prev, language: undefined }));
                              } else {
                                const opt = languageOptions.find((l) => l.value === val);
                                setFilters((prev) => ({
                                  ...prev,
                                  language: opt ? ([opt] as any) : undefined,
                                }));
                              }
                            }}
                            sx={getFilterSelectSx(Boolean(languageFilterValue))}
                          >
                            <MenuItem value="">
                              <em style={{ fontStyle: 'normal', color: '#9CA3AF' }}>Language</em>
                            </MenuItem>
                            {languageOptions.map((l) => (
                              <MenuItem key={l.value} value={l.value}>
                                {l.label}
                              </MenuItem>
                            ))}
                          </Select>
                          <SelectClearButton
                            show={Boolean(languageFilterValue)}
                            label="language filter"
                            onClear={() => {
                              setPage(0);
                              setFilters((prev) => ({ ...prev, language: undefined }));
                            }}
                          />
                        </FormControl>
                      </FilterCell>

                      {/* Availability */}
                      <FilterCell>
                        <FormControl variant="standard" fullWidth sx={{ position: 'relative' }}>
                          <Select
                            value={availabilityFilterValue}
                            onChange={(e) => {
                              setPage(0);
                              const val = e.target.value as 'all' | 'sync' | 'async';
                              setFilters((prev) => ({
                                ...prev,
                                availability:
                                  val === 'all'
                                    ? ['CaseType.video_call_instant', 'CaseType.one_report']
                                    : val === 'sync'
                                    ? ['CaseType.video_call_instant']
                                    : ['CaseType.one_report'],
                                synchronous: val === 'all' || val === 'sync',
                                asynchronous: val === 'all' || val === 'async',
                              }));
                            }}
                            sx={getFilterSelectSx(availabilityFilterValue !== 'all')}
                          >
                            <MenuItem value="all">Sync or Async</MenuItem>
                            <MenuItem value="sync">Sync only</MenuItem>
                            <MenuItem value="async">Async only</MenuItem>
                          </Select>
                          <SelectClearButton
                            show={availabilityFilterValue !== 'all'}
                            label="availability filter"
                            onClear={() => {
                              setPage(0);
                              setFilters((prev) => ({
                                ...prev,
                                availability: ['CaseType.video_call_instant', 'CaseType.one_report'],
                                synchronous: true,
                                asynchronous: true,
                              }));
                            }}
                          />
                        </FormControl>
                      </FilterCell>

                      <FilterCell>
                        <span />
                      </FilterCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {dataLoading ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Loading dotted />
                        </TableCell>
                      </TableRow>
                    ) : paginatedData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4, color: '#6B7280' }}>
                          No providers found
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedData.map((p) => {
                        const provider = p.user;
                        const isSelected = selectedProvider === provider.id;
                        return (
                          <TableRow
                            key={provider.id}
                            onClick={() => handleSelectedProvider(provider.id)}
                            sx={{
                              cursor: 'pointer',
                              backgroundColor: isSelected ? '#f1f8fe' : '#fff',
                              '&:hover': { backgroundColor: isSelected ? '#e3f2fd' : '#f9fafb' },
                              height: 68,
                            }}
                          >
                            {/* Name */}
                            <TableCell sx={bodyCellSx}>
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setTargetProvider(provider);
                                  setProviderProfileCardModal(true);
                                }}
                              >
                                <img
                                  alt="Avatar"
                                  onError={(event: any) => {
                                    event.target.src =
                                      'https://media.istockphoto.com/id/1130884625/tr/vekt%C3%B6r/kullan%C4%B1c%C4%B1-%C3%BCyesi-vekt%C3%B6r-simgesi-ui-kullan%C4%B1c%C4%B1-arabirimi-veya-profil-face-avatar-uygulamas%C4%B1.jpg?s=612x612&w=0&k=20&c=jAf5nq1ebnZo8TJbjaB9dMHMvgff7uOk67NkF5CpgB0=';
                                    event.target.onError = null;
                                  }}
                                  className={`fs-exclude ${styles.providerImage}`}
                                  src={getUserAvatar(provider.id)}
                                />
                                <span className="fs-exclude" style={{ whiteSpace: 'nowrap' }}>
                                  {provider.full_name}
                                  {provider.provider_profile?.credentials
                                    ? `, ${provider.provider_profile.credentials}`
                                    : ''}
                                </span>
                              </Box>
                            </TableCell>

                            {/* License state */}
                            <TableCell sx={{ ...bodyCellSx, maxWidth: 120 }}>
                              <span className="fs-exclude">
                                {getLicenseStates(provider.provider_profile?.licenses || [])}
                              </span>
                            </TableCell>

                            {/* Specialty */}
                            <TableCell sx={bodyCellSx}>
                              <span className="fs-unmask">
                                {provider.provider_profile?.specialty?.join(', ') || ''}
                              </span>
                            </TableCell>

                            {/* Insurance */}
                            <TableCell sx={{ ...bodyCellSx, maxWidth: 180 }}>
                              <span
                                className="fs-exclude"
                                style={{ whiteSpace: 'normal', wordBreak: 'break-word', fontSize: 13 }}
                              >
                                {getInsurances(provider.provider_profile?.licenses || [])}
                              </span>
                            </TableCell>

                            {/* Language */}
                            <TableCell sx={bodyCellSx}>
                              <span className="fs-mask">
                                {provider.basic_info?.languages
                                  ?.map((language) => typeof language !== 'string' && language.name)
                                  .join(', ')}
                              </span>
                            </TableCell>

                            {/* Availability */}
                            <TableCell sx={{ ...bodyCellSx, whiteSpace: 'normal' }}>
                              <Box display="flex" flexDirection="column" gap={0.25}>
                                {p.sync_time_intervals.map((av, i) => (
                                  <span
                                    key={`sync-${i}`}
                                    className="fs-unmask"
                                    style={{ whiteSpace: 'nowrap', fontSize: 12 }}
                                  >
                                    Sync: {moment(av.start_time).tz(selectedTimezone).format('MMM D, h:mmA')} –{' '}
                                    {moment(av.end_time).tz(selectedTimezone).format('MMM D, h:mmA')}
                                  </span>
                                ))}
                                {p.async_time_intervals.map((av, i) => (
                                  <span
                                    key={`async-${i}`}
                                    className="fs-unmask"
                                    style={{ whiteSpace: 'nowrap', fontSize: 12 }}
                                  >
                                    Async: {moment(av.start_time).tz(selectedTimezone).format('MMM D, h:mmA')} –{' '}
                                    {moment(av.end_time).tz(selectedTimezone).format('MMM D, h:mmA')}
                                  </span>
                                ))}
                              </Box>
                            </TableCell>

                            {/* Select */}
                            <TableCell sx={{ ...bodyCellSx, textAlign: 'right', width: 48, pr: '12px' }}>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectedProvider(provider.id);
                                }}
                                aria-label="Select Provider"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                className="fs-unmask"
                              >
                                <FaCheckCircle
                                  style={{ fontSize: 24 }}
                                  className={isSelected ? styles.primaryColor : styles.defaultColor}
                                />
                              </button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={filteredData.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(+e.target.value);
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50]}
                sx={{ '& .MuiTablePagination-toolbar': { minHeight: 40, padding: '0 8px', alignItems: 'baseline' } }}
              />
            </Paper>
          </div>
        </div>
      </div>

      {providerProfileCardModal && (
        <ProviderProfileCardModal
          isOpen={providerProfileCardModal}
          toggleModal={setProviderProfileCardModal}
          providerIdentity={targetProvider}
          patientCurrentStateValue={undefined}
          caseId={thisCase.id}
          filters={filters}
          thisCase={thisCase}
          setCase={setCase}
          setLoading={setLoading}
          openModal={openModal}
          isAssignProvider
        />
      )}

      <ConfirmationModal
        isOpen={showConfirmDialog}
        title="Reassign Provider"
        content={`Are you sure you want to reassign this case to ${selectedProviderName}?`}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={() => {
          setShowConfirmDialog(false);
          openModal(false);
          assignProvider(selectedProvider);
        }}
        isSubmitDisabled={false}
      />
    </div>
  );
};

export default ProviderListTable;
